// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";

import ZoomIn from "@insite/mobius/Icons/ZoomIn";
import ZoomOut from "@insite/mobius/Icons/ZoomOut";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import Close from "@toolkit/Library/Components/Icons/Close";
import Refresh from "@toolkit/Library/Components/Icons/Refresh";
import throttle from "lodash/throttle";
import qs from "qs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

interface ZoomImageProps {
    src: string;
    alt: string;
    width?: number;
    handleClose?: () => void;
    maxHeight?: string;
    maxWidth?: string;
}

const maxScale = 3.5;
const minScale = 0.5;
const startingScale = 1;

const ZoomImage: React.FC<ZoomImageProps> = ({ src, alt, handleClose, maxHeight, maxWidth }) => {
    const [scale, setScale] = useState(startingScale);
    const [isScrollDisabled, setIsScrollDisabled] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [translate, setTranslate] = useState({ x: 0, y: 0 });

    const lastPosition = useRef({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const { width: containerWidth, height: containerHeight } =
        containerRef.current?.parentElement?.getBoundingClientRect() ?? { width: 0, height: 0 };

    const maxTranslateX = (containerWidth * 1.2) / scale;
    const maxTranslateY = (containerHeight * 1.2) / scale;
    useEffect(() => {
        const handleScroll = (event: Event) => {
            if (isScrollDisabled) {
                event.preventDefault();
            }
        };

        const handleWheel = (event: WheelEvent) => {
            if (isScrollDisabled) {
                event.preventDefault();
            }
        };

        window.addEventListener("scroll", handleScroll, { passive: false });
        window.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("wheel", handleWheel);
        };
    }, [isScrollDisabled]);

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        setIsDragging(true);
        lastPosition.current = { x: event.clientX, y: event.clientY };
    };

    const handleMouseMove = useCallback(
        throttle((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            if (!isDragging || !containerRef.current) {
                return;
            }

            if (!containerWidth || !containerHeight) {
                return;
            }

            const deltaX = event.clientX - lastPosition.current.x;
            const deltaY = event.clientY - lastPosition.current.y;
            lastPosition.current = { x: event.clientX, y: event.clientY };

            setTranslate(prev => {
                let newX = prev.x + deltaX;
                let newY = prev.y + deltaY;

                newX = Math.max(-maxTranslateX, Math.min(newX, maxTranslateX));
                newY = Math.max(-maxTranslateY, Math.min(newY, maxTranslateY));

                return { x: newX, y: newY };
            });
        }, 16),
        [isDragging, scale],
    );

    const handleMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
        setIsDragging(false);
        lastPosition.current = { x: event.clientX, y: event.clientY };
    };

    const queryString = /\?.*/.exec(src);
    let aspectRatio = 1;
    if (queryString) {
        const obj = qs.parse(queryString[0], { ignoreQueryPrefix: true });
        if (obj.width && obj.height) {
            aspectRatio = obj.width / obj.height;
        }
    }

    const handleMouseWheel = (event: React.WheelEvent<HTMLDivElement>) => {
        const newScale = Number((scale + event.deltaY * -0.002).toFixed(2));
        if (newScale < minScale || newScale > maxScale) {
            return;
        }
        handleScaleChange(newScale);
    };
    const handleScaleChange = (newScale: number) => {
        if (newScale < minScale || newScale > maxScale) {
            return;
        }
        setScale(newScale);
        const newMaxTranslateX = (containerWidth * 0.5) / newScale;
        const newMaxTranslateY = (containerHeight * 0.5) / newScale;
        const newX = Math.max(-newMaxTranslateX, Math.min(translate.x, newMaxTranslateX));
        const newY = Math.max(-newMaxTranslateY, Math.min(translate.y, newMaxTranslateY));
        if (newX !== translate.x || newY !== translate.y) {
            setTranslate({ x: newX, y: newY });
        }
    };

    const handleZoomIn = (event?: React.MouseEvent<HTMLButtonElement>) => {
        event?.preventDefault();
        event?.stopPropagation();
        const newScale = Math.min(scale + 0.2, maxScale);
        handleScaleChange(newScale);
    };

    const handleZoomOut = (event?: React.MouseEvent<HTMLButtonElement>) => {
        event?.preventDefault();
        event?.stopPropagation();
        const newScale = Math.max(scale - 0.2, minScale);
        handleScaleChange(newScale);
    };

    const hasChanged = scale !== startingScale || translate.x !== 0 || translate.y !== 0;

    return (
        <StyledWrapper
            css={css`
                max-height: ${maxHeight || "90vh"};
                max-width: ${maxWidth || "80vw"};
                overflow: hidden;
                position: relative;
            `}
        >
            <ButtonRow>
                {hasChanged && (
                    <ZoomButton
                        onClick={() => {
                            setScale(startingScale);
                            setTranslate({ x: 0, y: 0 });
                        }}
                        aria-label="Reset Zoom"
                    >
                        <Refresh />
                    </ZoomButton>
                )}

                <ZoomButton onClick={handleZoomIn} aria-label="Zoom In">
                    <ZoomIn />
                </ZoomButton>
                <ZoomButton onClick={handleZoomOut} aria-label="Zoom Out">
                    <ZoomOut />
                </ZoomButton>
                {handleClose && (
                    <ZoomButton onClick={handleClose} aria-label="Close">
                        <Close />
                    </ZoomButton>
                )}
            </ButtonRow>
            <StyledWrapper
                css={css`
                    overflow: hidden;
                    aspect-ratio: ${aspectRatio.toString()};
                    position: relative;
                `}
            >
                <ZoomContainer
                    maxHeight={maxHeight || "90vh"}
                    maxWidth={maxWidth || "80vw"}
                    ref={containerRef}
                    onWheel={handleMouseWheel}
                    onMouseMove={handleMouseMove}
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onMouseEnter={() => setIsScrollDisabled(true)}
                    style={{
                        transform: `scale(${scale}) translate(${translate.x}px, ${translate.y}px)`,
                        cursor: isDragging ? "grabbing" : "grab",
                    }}
                    scale={scale}
                >
                    <StyledImg
                        src={src}
                        maxHeight={maxHeight || "90vh"}
                        maxWidth={maxWidth || "80vw"}
                        alt={alt}
                        draggable={false}
                    />
                </ZoomContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const ZoomContainer = styled.div<{ scale: number; maxHeight: string; maxWidth: string }>`
    height: 100%;
    max-height: ${props => props.maxHeight};
    max-width: ${props => props.maxWidth};
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    cursor: grab;
    transform-origin: center center;
    transition: transform 0.3s ease;
    will-change: transform;
    margin-bottom: ${pxToRem(16)};
    user-select: none;
    padding: ${pxToRem(100)};
`;

const StyledImg = styled.img<{ maxWidth: string; maxHeight: string }>`
    max-width: ${props => props.maxWidth};
    max-height: ${props => props.maxHeight};
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
    will-change: transform;
    pointer-events: none;
`;

const ZoomButton = styled.button`
    position: relative;

    width: 36px;
    background: white;
    height: 36px;
    border: none;
    border-radius: 6px;
    color: ${getColor("primary")};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 1;

    :hover {
        transform: scale(1.1);
        color: ${getColor("secondary")};
    }
`;

const ButtonRow = styled.div`
    padding: 1rem;
    width: fit-content;
    margin-left: auto;
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    z-index: 10000;
    gap: 1.25rem;
`;

export default ZoomImage;
